import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  Panel,
  useReactFlow,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { toast } from 'sonner';

import { TriggerNode } from './nodes/TriggerNode';
import { ActionNode } from './nodes/ActionNode';
import { DelayNode } from './nodes/DelayNode';
import { ConditionNode } from './nodes/ConditionNode';
import { EndNode } from './nodes/EndNode';
import WaitForResponseNode from './nodes/WaitForResponseNode';
import ButtonRouterNode from './nodes/ButtonRouterNode';
import UpdateDataNode from './nodes/UpdateDataNode';
import DeletableEdge from './edges/DeletableEdge';
import { NodePalette } from './NodePalette';
import { NodeConfigPanel } from './NodeConfigPanel';
import TestRunDialog from './TestRunDialog';

// Define custom node types
const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  delay: DelayNode,
  condition: ConditionNode,
  end: EndNode,
  wait_for_response: WaitForResponseNode,
  button_router: ButtonRouterNode,
  update_data: UpdateDataNode,
};

const edgeTypes = {
  deletable: DeletableEdge,
};

// Default edge options
const defaultEdgeOptions = {
  type: 'deletable',
  animated: true,
  style: { stroke: '#d4af37', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#d4af37',
  },
};

// Connection line style
const connectionLineStyle = {
  stroke: '#d4af37',
  strokeWidth: 2,
};

// Grid snap size
const snapGrid = [20, 20];

// Helper to generate unique IDs
const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

function JourneyCanvasInner({
  initialNodes = [],
  initialEdges = [],
  onSave,
  journey,
  templates,
  isReadOnly = false,
  showAnalytics = false,
  analytics = null,
}) {
  const reactFlowWrapper = useRef(null);
  const { project, fitView, getNodes, getEdges } = useReactFlow();
  
  // State for nodes and edges
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  
  // Selected node for configuration panel
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);
  
  // Track auto-save status
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const autoSaveTimerRef = useRef(null);
  const lastSaveRef = useRef(null);

  // Test Run dialog open/close
  const [testRunOpen, setTestRunOpen] = useState(false);
  
  // IMPORTANT: Sync nodes/edges when initialNodes/initialEdges change (e.g., when journey loads)
  // This fixes the bug where saving after load would erase data
  const initialNodesKey = JSON.stringify(initialNodes.map(n => n.id).sort());
  const initialEdgesKey = JSON.stringify(initialEdges.map(e => e.id).sort());
  
  useEffect(() => {
    if (initialNodes.length > 0) {
      setNodes(initialNodes);
      // Force all loaded edges to use the deletable edge type so the X button
      // appears on historical journeys too.
      setEdges(initialEdges.map((e) => ({ ...e, type: 'deletable' })));
      setHasChanges(false);
      // Reset auto-save reference
      lastSaveRef.current = null;
    }
  }, [initialNodesKey, initialEdgesKey, setNodes, setEdges]);
  
  // Debounced auto-save function (DISABLED — auto-save was causing race conditions
  // with rename, so we now rely on explicit manual Save via the header button).
  const triggerAutoSave = useCallback(() => {
    // No-op. Kept so the deps array of the watcher useEffect below stays valid.
  }, []);
  
  // Watch for changes - only tracks "unsaved changes" indicator now (auto-save disabled)
  useEffect(() => {
    if (hasChanges && !isReadOnly) {
      setAutoSaveStatus('changes');
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasChanges, isReadOnly]);

  // Wrapped handlers to track changes
  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    // Track position changes (from dragging)
    const hasPositionChange = changes.some(c => c.type === 'position' && c.dragging === false);
    if (hasPositionChange) {
      setHasChanges(true);
    }
  }, [onNodesChange]);

  const handleEdgesChange = useCallback((changes) => {
    onEdgesChange(changes);
    // Track edge removals
    const hasRemoval = changes.some(c => c.type === 'remove');
    if (hasRemoval) {
      setHasChanges(true);
    }
  }, [onEdgesChange]);

  // Handle connection between nodes
  const onConnect = useCallback(
    (params) => {
      // Source handles that are designed to fan out to multiple targets
      // (each sub-handle already corresponds to a distinct outcome).
      const fanOutHandles = new Set(['yes', 'no', 'received', 'timeout']);
      
      // Prevent multiple connections from the SAME source handle. Each outcome
      // handle is a single path — to branch, the user should use a Condition
      // or Button Router node.
      const sourceHasConnection = edges.some(
        (edge) => edge.source === params.source && edge.sourceHandle === params.sourceHandle
      );
      
      if (sourceHasConnection && !fanOutHandles.has(params.sourceHandle)) {
        // Button Router: each per-button handle already has its own id — allow it
        // since different sourceHandles are different buttons.
        const sourceNode = getNodes().find((n) => n.id === params.source);
        if (sourceNode?.type !== 'button_router') {
          toast.warning(
            'This outcome already has a next step. Add a Condition or Button Router ' +
            'node to branch into multiple paths.'
          );
          return;
        }
      }
      
      setEdges((eds) => addEdge({
        ...params,
        ...defaultEdgeOptions,
      }, eds));
      setHasChanges(true);
    },
    [edges, setEdges, getNodes]
  );

  // Validate connection before allowing
  const isValidConnection = useCallback((connection) => {
    // Prevent self-connections
    if (connection.source === connection.target) return false;
    
    // Get source and target nodes
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) return false;
    
    // Can't connect TO a trigger node
    if (targetNode.type === 'trigger') return false;
    
    // Can't connect FROM an end node
    if (sourceNode.type === 'end') return false;
    
    return true;
  }, [nodes]);

  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  // Handle canvas click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Handle drag over for dropping new nodes
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop of new node
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow/type');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/reactflow/data') || '{}');

      if (!type) return;

      const position = project({
        x: event.clientX - reactFlowWrapper.current.getBoundingClientRect().left,
        y: event.clientY - reactFlowWrapper.current.getBoundingClientRect().top,
      });

      // Snap to grid
      position.x = Math.round(position.x / snapGrid[0]) * snapGrid[0];
      position.y = Math.round(position.y / snapGrid[1]) * snapGrid[1];

      // A journey has exactly ONE entry trigger. If the user drags a new trigger
      // from the palette and another trigger already exists on the canvas,
      // REPLACE it (keep the old node's id + wiring, just swap trigger_type /
      // label / icon). This avoids creating an orphaned second trigger that
      // can't be deleted (trigger nodes are pinned via Delete/Backspace guard).
      if (type === 'trigger') {
        const existingTrigger = nodes.find((n) => n.type === 'trigger');
        if (existingTrigger) {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === existingTrigger.id
                ? { ...n, data: { ...n.data, ...nodeData, label: nodeData.label || n.data.label } }
                : n
            )
          );
          setHasChanges(true);
          setSelectedNode((prev) => {
            const updated = { ...existingTrigger, data: { ...existingTrigger.data, ...nodeData } };
            return updated;
          });
          return;
        }
      }

      const newNode = {
        id: generateId(),
        type,
        position,
        data: {
          ...nodeData,
          label: nodeData.label || type.charAt(0).toUpperCase() + type.slice(1),
        },
      };

      setNodes((nds) => [...nds, newNode]);
      setHasChanges(true);
      setSelectedNode(newNode);
    },
    [project, setNodes, nodes]
  );

  // Update node data from config panel
  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, ...newData },
          };
        }
        return node;
      })
    );
    setHasChanges(true);
    
    // Update selected node reference
    setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, ...newData } } : null);
  }, [setNodes]);

  // Delete selected node
  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setSelectedNode(null);
    setHasChanges(true);
  }, [setNodes, setEdges]);

  // Handle keyboard shortcuts
  const onKeyDown = useCallback((event) => {
    // Don't delete nodes if user is typing in an input field
    const tagName = event.target.tagName.toLowerCase();
    const isInputElement = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || event.target.isContentEditable;
    
    if (isInputElement) {
      // Allow normal typing/backspace in input fields
      return;
    }
    
    if ((event.key === 'Delete' || event.key === 'Backspace') && selectedNode) {
      // Don't delete trigger node
      if (selectedNode.type !== 'trigger') {
        deleteNode(selectedNode.id);
      }
    }
  }, [selectedNode, deleteNode]);

  // Convert to JSON for saving
  const getJourneyJson = useCallback(() => {
    const currentNodes = getNodes();
    const currentEdges = getEdges();
    
    // Build flow structure
    const flow = {
      nodes: currentNodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: node.data,
      })),
      edges: currentEdges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
      })),
    };
    
    return flow;
  }, [getNodes, getEdges]);

  // Save journey
  const handleSave = useCallback(() => {
    const flowJson = getJourneyJson();
    
    // Safety check: Don't save if no nodes (prevents accidental data erasure)
    if (!flowJson.nodes || flowJson.nodes.length === 0) {
      console.error('Cannot save journey: No nodes found. This may indicate a loading issue.');
      toast.error('Cannot save: Journey data not loaded properly. Please refresh the page.');
      return;
    }
    
    if (onSave) {
      onSave(flowJson);
      setHasChanges(false);
    }
  }, [getJourneyJson, onSave]);

  // Close config panel
  const closeConfigPanel = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Enrich nodes with analytics data when analytics are shown
  const nodesWithAnalytics = useMemo(() => {
    if (!showAnalytics || !analytics?.node_stats) {
      return nodes;
    }
    
    return nodes.map(node => {
      const nodeStats = analytics.node_stats[node.id];
      if (nodeStats) {
        return {
          ...node,
          data: {
            ...node.data,
            showAnalytics: true,
            analytics: nodeStats,
          },
        };
      }
      return {
        ...node,
        data: {
          ...node.data,
          showAnalytics: true,
          analytics: null,
        },
      };
    });
  }, [nodes, showAnalytics, analytics]);

  return (
    <div 
      className="flex h-full w-full" 
      onKeyDown={onKeyDown} 
      tabIndex={0}
    >
      {/* Node Palette - Left Side */}
      {!isReadOnly && (
        <NodePalette />
      )}
      
      {/* Canvas */}
      <div 
        className="flex-1 h-full bg-wt-bg" 
        ref={reactFlowWrapper}
      >
        <ReactFlow
          nodes={nodesWithAnalytics}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          connectionLineStyle={connectionLineStyle}
          isValidConnection={isValidConnection}
          snapToGrid={true}
          snapGrid={snapGrid}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1}
          maxZoom={2}
          deleteKeyCode={null} // We handle delete ourselves
          className="bg-wt-bg"
        >
          <Background 
            color="#333" 
            gap={20} 
            size={1}
            variant="dots"
          />
          <Controls 
            className="bg-wt-surface border-wt-border"
            showInteractive={false}
          />
          <MiniMap 
            nodeColor={(node) => {
              switch (node.type) {
                case 'trigger': return '#22c55e';
                case 'action': return '#d4af37';
                case 'delay': return '#3b82f6';
                case 'condition': return '#a855f7';
                case 'end': return '#ef4444';
                default: return '#666';
              }
            }}
            maskColor="rgba(0, 0, 0, 0.8)"
            className="bg-wt-surface border border-wt-border rounded-lg"
          />
          
          {/* Save Button Panel */}
          <Panel position="top-right" className="flex gap-2 items-center">
            {/* Auto-save status indicator */}
            {autoSaveStatus === 'changes' && (
              <span className="text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded animate-pulse">
                Unsaved changes — click Save
              </span>
            )}
            {/* Test Run — only shown when we have a persisted journey id */}
            {journey?.id && (
              <button
                onClick={() => setTestRunOpen(true)}
                className="px-3 py-2 bg-wt-subtle border border-wt-border text-wt-text-primary rounded-lg hover:bg-wt-bg flex items-center gap-1.5 text-sm"
                data-testid="journey-test-run-btn"
                title="Dry-run this journey against a real order/customer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-wt-gold" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Test Run
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isReadOnly}
              className="px-4 py-2 bg-wt-gold text-black font-medium rounded-lg hover:bg-wt-gold/90 disabled:opacity-50 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              Save Journey
            </button>
          </Panel>
        </ReactFlow>
      </div>

      {/* Test Run Dialog */}
      <TestRunDialog
        journey={journey}
        open={testRunOpen}
        onClose={() => setTestRunOpen(false)}
      />
      
      {/* Configuration Panel - Right Side */}
      {selectedNode && (
        <NodeConfigPanel
          node={selectedNode}
          onUpdate={updateNodeData}
          onDelete={deleteNode}
          onClose={closeConfigPanel}
          templates={templates}
          isReadOnly={isReadOnly}
        />
      )}
    </div>
  );
}

// Wrapper with provider
export function JourneyCanvas(props) {
  return (
    <ReactFlowProvider>
      <JourneyCanvasInner {...props} />
    </ReactFlowProvider>
  );
}

export default JourneyCanvas;
